import React from "react";

import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { supabase } from "~/integrations/supabase/client";
import AmountInput from "~/pages/payments/components/AmountInput";
import ClientSelector from "~/pages/payments/components/ClientSelector";
import ManualFacturaSelector from "~/pages/payments/components/ManualFacturaSelector";
import SalesRequestSelector from "~/pages/payments/components/SalesRequestSelector";
import SubmitButton from "~/pages/payments/components/SubmitButton";

// Chilean banks list
const CHILEAN_BANKS = [
  "Banco de Chile",
  "Banco Santander Chile",
  "BCI",
  "Banco Estado",
  "Banco BICE",
  "Banco de Crédito e Inversiones",
  "Banco Falabella",
  "Banco Security",
  "Banco Ripley",
  "Scotiabank Chile",
  "Banco Itaú Chile",
  "Banco Consorcio",
  "Banco Internacional",
  "Banco BTG Pactual Chile",
  "Banco Edwards Citi",
  "Banco Paris",
  "Banco Penta",
  "Banco Promerica",
  "Banco Coopeuch",
  "Otro",
];

// Form schema for validation
const formSchema = z
  .object({
    amount: z
      .number()
      .min(1, "El monto debe ser mayor a 0")
      .refine((val) => !isNaN(val), {
        message: "El monto debe ser un número válido",
      }),
    clientId: z.string().uuid("Por favor seleccione un cliente"),
    paymentType: z.enum(
      ["sales_request", "manual_factura", "general", "check"],
      {
        required_error: "Por favor seleccione un tipo de pago",
      }
    ),
    salesRequestId: z.string().uuid().optional(),
    manualFacturaId: z.string().uuid().optional(),
    // Check-specific fields
    checkNumber: z.string().optional(),
    bank: z.string().optional(),
    receptionDate: z.date().optional(),
    collectionDate: z.date().optional(),
  })
  .refine(
    (data) => {
      if (data.paymentType === "sales_request" && !data.salesRequestId) {
        return false;
      }
      if (data.paymentType === "manual_factura" && !data.manualFacturaId) {
        return false;
      }
      if (data.paymentType === "check") {
        if (
          !data.salesRequestId ||
          !data.checkNumber ||
          !data.bank ||
          !data.receptionDate ||
          !data.collectionDate
        ) {
          return false;
        }
      }
      return true;
    },
    {
      message: "Por favor complete todos los campos requeridos",
      path: ["salesRequestId"], // This will show the error on both fields
    }
  );

type FormValues = z.infer<typeof formSchema>;

const ManualPaymentForm: React.FC = () => {
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: undefined,
      clientId: undefined,
      paymentType: "sales_request",
      salesRequestId: undefined,
      manualFacturaId: undefined,
      checkNumber: undefined,
      bank: undefined,
      receptionDate: undefined,
      collectionDate: undefined,
    },
  });

  const isSubmitting = form.formState.isSubmitting;
  const selectedClient = form.watch("clientId");
  const paymentType = form.watch("paymentType");

  const onSubmit = async (values: FormValues) => {
    try {
      if (values.paymentType === "manual_factura" && values.manualFacturaId) {
        // Handle manual factura payment with direct database operations

        // First, get the current manual factura data
        const { data: facturaData, error: fetchError } = await supabase
          .from("manual_facturas")
          .select("paid, dues, balance")
          .eq("id", values.manualFacturaId)
          .single();

        if (fetchError) {
          console.error("Error fetching manual factura:", fetchError);
          toast.error("Error al obtener datos de la factura");
          return;
        }

        // Create the payment record
        const { data: paymentData, error: paymentError } = await supabase
          .from("payments")
          .insert({
            amount: values.amount,
            client: values.clientId,
            manual_factura_id: values.manualFacturaId,
            payment_method: "manual",
            reference: "Pago manual registrado",
          })
          .select();

        if (paymentError) {
          console.error("Error creating payment:", paymentError);
          toast.error("Error al registrar el pago");
          return;
        }

        // Calculate new paid amount using RPC function that excludes checks in portfolio
        const { data: newPaidAmount, error: paidAmountError } =
          await supabase.rpc("get_manual_factura_paid_amount", {
            factura_id: values.manualFacturaId,
          });

        if (paidAmountError) {
          console.error("Error calculating paid amount:", paidAmountError);
          toast.error("Error al calcular el monto pagado");
          return;
        }

        const duesAmount = facturaData.dues || 0;
        const newBalance = duesAmount - (newPaidAmount || 0);

        // Update the manual factura with new paid amount and balance
        const { error: updateError } = await supabase
          .from("manual_facturas")
          .update({
            paid: newPaidAmount || 0,
            balance: newBalance,
          })
          .eq("id", values.manualFacturaId);

        if (updateError) {
          console.error("Error updating manual factura:", updateError);
          // If update fails, try to rollback the payment
          await supabase.from("payments").delete().eq("id", paymentData[0].id);
          toast.error("Error al actualizar la factura");
          return;
        }

        toast.success("Pago registrado exitosamente para factura manual");

        // Dispatch events to update related components
        window.dispatchEvent(new CustomEvent("manual-facturas-updated"));
        window.dispatchEvent(
          new CustomEvent("payment-assigned-to-manual-factura", {
            detail: { clientId: values.clientId },
          })
        );
      } else if (
        values.paymentType === "sales_request" &&
        values.salesRequestId
      ) {
        // Handle sales request payment (existing logic)
        const { data, error } = await supabase
          .from("payments")
          .insert({
            amount: values.amount,
            client: values.clientId,
            sales_request: values.salesRequestId,
            payment_method: "manual",
          })
          .select();

        if (error) {
          console.error("Error creating payment:", error);
          toast.error("Error al registrar el pago");
          return;
        }

        // Update sales_request paid_amount and paid status
        const { data: salesRequestData, error: fetchError } = await supabase
          .from("sales_request")
          .select("paid_amount, total_amount, proportional_interest, status")
          .eq("id", values.salesRequestId)
          .single();

        if (fetchError) {
          console.error("Error fetching sales request:", fetchError);
          toast.error(
            "El pago se creó pero no se pudo actualizar la nota de venta"
          );
          return;
        }

        // Use RPC function to get accurate paid amount (excluding checks in portfolio)
        const { data: newPaidAmount, error: paidAmountError } =
          await supabase.rpc("get_sales_request_paid_amount", {
            sales_request_id: values.salesRequestId,
          });

        if (paidAmountError) {
          console.error("Error calculating paid amount:", paidAmountError);
          toast.error("Error al calcular el monto pagado");
          return;
        }
        const proportionalInterest = Number(
          salesRequestData.proportional_interest || 0
        );
        const totalPlusInterest =
          Number(salesRequestData.total_amount) + proportionalInterest;
        const isPaid = newPaidAmount >= totalPlusInterest;

        // Update sales_request based on status
        const updateData: any = {
          paid_amount: newPaidAmount,
        };

        if (salesRequestData.status === "prepaid") {
          // For prepaid orders, keep in prepaid status but prevent auto-paid
          updateData.paid = false; // Explicitly prevent auto-paid for prepaid workflow
        } else {
          // For non-prepaid orders, update paid status normally
          updateData.paid = isPaid;
        }

        const { error: updateError } = await supabase
          .from("sales_request")
          .update(updateData)
          .eq("id", values.salesRequestId);

        if (updateError) {
          console.error("Error updating sales request:", updateError);
          toast.error(
            "El pago se creó pero no se pudo actualizar la nota de venta"
          );
          return;
        }

        toast.success("Pago registrado exitosamente para nota de venta");
      } else if (values.paymentType === "check" && values.salesRequestId) {
        // Handle check payment for sales request
        const { data, error } = await supabase
          .from("payments")
          .insert({
            amount: values.amount,
            client: values.clientId,
            sales_request: values.salesRequestId,
            payment_method: "check",
            reference: `Cheque #${values.checkNumber} - ${values.bank} (En cartera hasta ${format(values.collectionDate!, "dd/MM/yyyy", { locale: es })})`,
            check_number: values.checkNumber,
            bank: values.bank,
            reception_date: values.receptionDate?.toISOString(),
            collection_date: values.collectionDate?.toISOString(),
            check_status: "in_portfolio", // NEW: Set proper initial status for checks (English value)
          })
          .select();

        if (error) {
          console.error("Error creating check payment:", error);
          toast.error("Error al registrar el pago con cheque");
          return;
        }

        // Update sales_request paid_amount and paid status
        const { data: salesRequestData, error: fetchError } = await supabase
          .from("sales_request")
          .select("paid_amount, total_amount, proportional_interest, status")
          .eq("id", values.salesRequestId)
          .single();

        if (fetchError) {
          console.error("Error fetching sales request:", fetchError);
          toast.error(
            "El pago se creó pero no se pudo actualizar la nota de venta"
          );
          return;
        }

        const newPaidAmount =
          (salesRequestData.paid_amount || 0) + values.amount;
        const proportionalInterest = Number(
          salesRequestData.proportional_interest || 0
        );
        const totalPlusInterest =
          Number(salesRequestData.total_amount) + proportionalInterest;
        const isPaid = newPaidAmount >= totalPlusInterest;

        // Update sales_request based on status
        const updateData: any = {
          paid_amount: newPaidAmount,
        };

        if (salesRequestData.status === "prepaid") {
          // For prepaid orders, keep in prepaid status but prevent auto-paid
          updateData.paid = false; // Explicitly prevent auto-paid for prepaid workflow
        } else {
          // For non-prepaid orders, update paid status normally
          updateData.paid = isPaid;
        }

        const { error: updateError } = await supabase
          .from("sales_request")
          .update(updateData)
          .eq("id", values.salesRequestId);

        if (updateError) {
          console.error("Error updating sales request:", updateError);
          toast.error(
            "El pago se creó pero no se pudo actualizar la nota de venta"
          );
          return;
        }

        // Show specific success message for checks
        toast.success(
          `Cheque #${values.checkNumber} registrado en cartera. El crédito se liberará cuando sea cobrado el ${format(values.collectionDate!, "dd/MM/yyyy", { locale: es })}.`
        );
      } else if (values.paymentType === "general") {
        // Handle general payment (no specific sales request or manual factura)
        const { data, error } = await supabase
          .from("payments")
          .insert({
            amount: values.amount,
            client: values.clientId,
            payment_method: "manual",
            reference: "Pago general registrado manualmente",
          })
          .select();

        if (error) {
          console.error("Error creating general payment:", error);
          toast.error("Error al registrar el pago general");
          return;
        }

        toast.success("Pago general registrado exitosamente");
      }

      // Reset form after successful submission
      form.reset();
      navigate("/payments-summary");
    } catch (error) {
      console.error("Error in form submission:", error);
      toast.error("Error inesperado al procesar el pago");
    }
  };

  const handleClientChange = (value: string) => {
    form.setValue("clientId", value);
    form.setValue("salesRequestId", undefined);
    form.setValue("manualFacturaId", undefined);
  };

  const handlePaymentTypeChange = (value: string) => {
    form.setValue(
      "paymentType",
      value as "sales_request" | "manual_factura" | "general" | "check"
    );
    form.setValue("salesRequestId", undefined);
    form.setValue("manualFacturaId", undefined);
    form.setValue("checkNumber", undefined);
    form.setValue("bank", undefined);
    form.setValue("receptionDate", undefined);
    form.setValue("collectionDate", undefined);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Monto</FormLabel>
              <FormControl>
                <AmountInput value={field.value} onChange={field.onChange} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="clientId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Cliente</FormLabel>
              <FormControl>
                <ClientSelector
                  value={field.value}
                  onChange={handleClientChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="paymentType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tipo de Pago</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={handlePaymentTypeChange}
                  value={field.value}
                  className="flex flex-col space-y-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sales_request" id="sales_request" />
                    <Label htmlFor="sales_request">Pago a Nota de Venta</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="manual_factura"
                      id="manual_factura"
                    />
                    <Label htmlFor="manual_factura">
                      Pago a Factura Manual
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="check" id="check" />
                    <Label htmlFor="check">Pago con Cheque</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="general" id="general" />
                    <Label htmlFor="general">
                      Pago General (sin documento específico)
                    </Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {(paymentType === "sales_request" || paymentType === "check") && (
          <FormField
            control={form.control}
            name="salesRequestId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nota de Venta</FormLabel>
                <FormControl>
                  <div className="mt-1 space-y-3">
                    <SalesRequestSelector
                      clientId={selectedClient}
                      value={field.value}
                      onChange={field.onChange}
                      error={form.formState.errors.salesRequestId?.message}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {paymentType === "manual_factura" && (
          <FormField
            control={form.control}
            name="manualFacturaId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Factura Manual</FormLabel>
                <FormControl>
                  <div className="mt-1 space-y-3">
                    <ManualFacturaSelector
                      clientId={selectedClient}
                      value={field.value}
                      onChange={field.onChange}
                      error={form.formState.errors.manualFacturaId?.message}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {paymentType === "check" && (
          <div className="space-y-4 rounded-lg border bg-gray-50 p-4">
            <h3 className="text-lg font-medium">Información del Cheque</h3>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="checkNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Número de cheque <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Ej: 123456"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bank"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Banco <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar banco" />
                        </SelectTrigger>
                        <SelectContent>
                          {CHILEAN_BANKS.map((bank) => (
                            <SelectItem key={bank} value={bank}>
                              {bank}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="receptionDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Fecha de recepción <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={`w-full justify-start text-left font-normal ${
                              !field.value && "text-muted-foreground"
                            }`}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? (
                              format(field.value, "dd/MM/yyyy", { locale: es })
                            ) : (
                              <span>Seleccionar fecha</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                            locale={es}
                          />
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="collectionDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Fecha para cobrar <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={`w-full justify-start text-left font-normal ${
                              !field.value && "text-muted-foreground"
                            }`}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? (
                              format(field.value, "dd/MM/yyyy", { locale: es })
                            ) : (
                              <span>Seleccionar fecha</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                            locale={es}
                          />
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        )}

        <SubmitButton isSubmitting={isSubmitting} className="w-full" />
      </form>
    </Form>
  );
};

export default ManualPaymentForm;
